import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { enqueueSnackbar } from "notistack";
import ThemeToggle from "../../ThemeToggle/ThemeToggle";
import { useTheme } from "../../../context/ThemeContext";
import {
  setCrossDomainCookie,
  clearAllCookies,
  getCookie,
} from "../../../utils/cookie-utils";
import {
  authxAppsBase,
  authxSessionHeaders,
  buildAuthxSessionGetUrl,
  mergeAuthxVerifyMfaSession,
  isVerifyMfaResponseSuccess,
  persistAuthxSessionCookies,
  clearPendingVerificationState,
  shouldActivateCreatedUser,
  activateCreatedUser,
} from "../../../utils/authx-login-helpers";
import {
  getAuthxAppsErrorMessage,
  isAuthxAppsOperationSuccess,
  parseAuthxAppsResponseBody,
  isAuthxMfaNotEnabledBody,
  isAuthxSessionExpiredBody,
} from "../../../utils/authx-apps-response";
import { getSafeRedirectUrl } from "../../../utils/navigation-utils";

const TwoFAPanel = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [userEmail, setUserEmail] = useState();
  const [loginSecuredHash, setLoginSecuredHash] = useState("");
  const [accessKey, setAccessKey] = useState("");
  const [refreshKey, setRefreshKey] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [smsSent, setSmsSent] = useState(false);

  const [redirectBackTo, setRedirectBackTo] = useState("");
  const [code, setCode] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);

  const inputRef = useRef();

  const handleCloseSuccess = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSuccess(false);
  };

  const handleVerification = async () => {
    setLoading(true);

    if (code.length === 6) {
      try {
        const ak = accessKey || location.state?.accessKey;
        const rk = refreshKey || location.state?.refreshKey;
        if (!ak || !rk || ak === "undefined" || rk === "undefined") {
          enqueueSnackbar("Session expired. Please log in again.", {
            variant: "error",
          });
          navigate("/");
          return;
        }

        const verificationUrl = `${authxAppsBase()}/verify-mfa`;

        const headers = authxSessionHeaders(ak, rk, "127.0.0.2");

        const verificationData = JSON.stringify({
          accessKey: ak,
          mfaCode: code,
          rememberMe: rememberMe ? "Y" : "N",
        });

        const verificationResponse = await fetch(verificationUrl, {
          method: "POST",
          headers,
          body: verificationData,
        });

        const verificationBody = await parseAuthxAppsResponseBody(
          verificationResponse,
        );

        if (isAuthxMfaNotEnabledBody(verificationBody)) {
          persistAuthxSessionCookies({ accessKey: ak, refreshKey: rk }, userEmail);
          setCrossDomainCookie("emailVerified", "Y");
          setCrossDomainCookie("phoneVerified", "Y");
          navigate("/mfa-setup", {
            state: { accessKey: ak, refreshKey: rk },
          });
          return;
        }

        if (isAuthxSessionExpiredBody(verificationBody)) {
          clearAllCookies();
          navigate("/");
          return;
        }

        const verificationJson =
          typeof verificationBody === "object" && verificationBody
            ? verificationBody
            : {};
        const merged = mergeAuthxVerifyMfaSession(
          { accessKey: ak, refreshKey: rk },
          verificationJson,
        );

        const verifyOk = isVerifyMfaResponseSuccess(
          verificationResponse.ok,
          verificationJson,
          merged,
        );

        if (verifyOk) {
          persistAuthxSessionCookies(
            {
              ...verificationJson,
              accessKey: merged.accessKey,
              refreshKey: merged.refreshKey,
            },
            userEmail,
          );

          if (shouldActivateCreatedUser(merged)) {
            const activation = await activateCreatedUser(
              getCookie("loginSecuredHash"),
            );
            if (!activation.ok) {
              setLoading(false);
              enqueueSnackbar(
                activation.errorMessage ||
                  "Account activation failed. Please try again.",
                { variant: "error" },
              );
              return;
            }
          }

          setCrossDomainCookie("databaseId", "0");
          clearPendingVerificationState();

          if (redirectBackTo) {
            window.location = getSafeRedirectUrl(
              redirectBackTo,
              window.location.origin,
            );
          } else {
            window.location = "/user-dashboard";
          }
        } else {
          if (verificationBody === "Your API Key expired") {
            clearAllCookies();
            navigate("/");
            return;
          }
          setLoading(false);
          setCode("");
          inputRef.current.focus();
        }
      } catch (error) {
        setCode("");
        inputRef.current.focus();
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const sendCodeViaSMS = async () => {
    const ak = accessKey || location.state?.accessKey;
    const rk = refreshKey || location.state?.refreshKey;

    if (
      !ak ||
      !rk ||
      ak === "" ||
      ak === "undefined" ||
      rk === "" ||
      rk === "undefined"
    ) {
      clearAllCookies();
      navigate("/");
      return;
    }

    try {
      setSmsSent(true);
      const smsUrl = buildAuthxSessionGetUrl("send-2fa-code-as-sms", ak, rk);

      setTimeout(async () => {
        const smsResponse = await fetch(smsUrl, {
          method: "GET",
          headers: authxSessionHeaders(ak, rk, "127.0.0.2"),
        });

        const smsJson = await smsResponse.json().catch(() => ({}));
        console.log(smsJson);
        if (isAuthxAppsOperationSuccess(smsResponse.ok, smsJson)) {
          setLoading(false);
          enqueueSnackbar("SMS sent successfully", { variant: "success" });
        } else {
          setSmsSent(false);
          enqueueSnackbar(
            getAuthxAppsErrorMessage(
              smsJson,
              "Failed to send SMS code. Please try again.",
            ),
            { variant: "error" },
          );
        }
      }, 10000);
    } catch (error) {
      setSmsSent(false);
      enqueueSnackbar(
        "Error occurred while sending the code. Please try again.",
        { variant: "error" },
      );
    }
  };

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (location.state?.email && !userEmail) {
      setUserEmail(location.state.email);
      setAccessKey(location.state.accessKey || "");
      setRefreshKey(location.state.refreshKey || "");
      setLoginSecuredHash(location.state.loginSecuredHash);
      setRedirectBackTo(
        location.state.redirectBackTo
          ? getSafeRedirectUrl(
              location.state.redirectBackTo,
              window.location.origin,
            )
          : "",
      );
    } else if (!location?.state?.email && !userEmail) {
      navigate("/");
    }
  }, [location.state, navigate, userEmail]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.altKey && event.key === "r") {
        setRememberMe((prevRememberMe) => !prevRememberMe);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Box className="glass-card" sx={{ p: { xs: 3.5, md: 4 }, width: "100%" }}>
      <Snackbar
        sx={{ zIndex: "9999" }}
        open={openSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        data-testid="two-fapanel--Snackbar-0"
      >
        <Alert
          onClose={handleCloseSuccess}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
          data-testid="two-fapanel--Alert-0"
        >
          The code has been sent via SMS
        </Alert>
      </Snackbar>

      <Typography variant="h2" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
        Two-factor authentication
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
        Enter the code from your authentication app for {userEmail}
      </Typography>

      <TextField
        type="tel"
        fullWidth
        value={code}
        onChange={(e) => {
          const newValue = e.target.value;
          if (/^\d*$/.test(newValue) && newValue.length <= 6) {
            setCode(newValue);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !loading) {
            handleVerification();
          }
        }}
        inputProps={{
          style: {
            textAlign: "center",
            fontSize: "16px",
            letterSpacing: "5px",
          },
        }}
        sx={{
          mb: 2,
          "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
          "input[type=number]": {
            MozAppearance: "textfield",
          },
        }}
        inputRef={inputRef}
        data-testid="two-fa-panel--TextField-0"
      />

      <FormControlLabel
        sx={{ mb: 2, span: { fontSize: 14 } }}
        control={
          <Checkbox
            name="rememberMe"
            checked={rememberMe}
            onChange={() => setRememberMe((prevRememberMe) => !prevRememberMe)}
            data-testid="two-fa-panel--Checkbox-0"
          />
        }
        label={
          <span>
            <u>R</u>emember Me
          </span>
        }
        data-testid="two-fapanel--FormControlLabel-0"
      />

      <Button
        variant="outlined"
        className="btn-glass"
        fullWidth
        disabled={loading || code.length !== 6}
        onClick={handleVerification}
        sx={{ mb: 3 }}
        data-testid="two-fa-panel--Button-0"
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Log In"}
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 3,
        }}
      >
        <Link
          className={"link " + smsSent ? "disabled" : ""}
          component="button"
          type="button"
          onClick={() => {
            if (!smsSent) sendCodeViaSMS();
          }}
          aria-disabled={smsSent}
          sx={{
            color: smsSent ? "text.disabled" : "primary.main",
            cursor: smsSent ? "default" : "pointer",
            fontSize: "14px",
            fontWeight: 600,
            textDecoration: "none",
            background: "none",
            border: "none",
            p: 0,
          }}
          data-testid="two-fa-panel--Link-0"
        >
          Get 2FA code via SMS
        </Link>
        <Link
          component="button"
          type="button"
          className="link"
          onClick={() => navigate("/")}
          sx={{
            color: "primary.main",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 600,
            textDecoration: "none",
            background: "none",
            border: "none",
            p: 0,
          }}
          data-testid="two-fa-panel--Link-1"
        >
          Log out
        </Link>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <ThemeToggle
          titlefontSize={16}
          title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        />
      </Box>
    </Box>
  );
};

export default TwoFAPanel;
